class FarloUiRevolverReviewCarousel {
    constructor(element, options = {}) {
        this.element = element;
        this.slides = Array.from(element.children);
        this.currentIndex = 0;
        this.startX = 0;
        this.dragStartX = 0;
        this.dragEndX = 0;
        this.visibleSlides = 1;
        this.totalVisibleSets = 0;
        this.isDragging = false;
        this.autoplayInterval = null;
        this.listenersAdded = false; // Ensure event listeners are only added once

        this.options = {
            arrows: true,
            auto: true,
            autoDuration: 3000,
            fade: false,
            lazyLoad: false,
            loop: true,
            pagination: true,
            swipe: true,
            ...options
        };

        this.handleKeyboardInput = this.handleKeyboardInput.bind(this);

        this.initialiseRevolver();
    }

    initialiseRevolver() {
        if (this.element.classList.contains('farlo-ui-revolver-carousel--loaded')) {
            return;
        }

        this.track = document.createElement('div');
        this.track.className = 'farlo-ui-revolver-carousel__cylinder';
        this.element.classList.add('farlo-ui-revolver-carousel');
        this.element.classList.add('farlo-ui-revolver-carousel--loaded');

        this.slides.forEach((slide) => {
            slide.classList.add('farlo-ui-revolver-carousel__chamber');
            this.track.appendChild(slide);
        });

        this.element.appendChild(this.track);

        // Initialize the carousel
        this.calculateVisibleSlides();
        this.updateTotalVisibleSets();
        this.updateCarousel();

        if (this.options.pagination) {
            this.createPagination();
        }

        if (this.options.arrows) {
            this.createArrows();
        }

        if (this.options.fade) {
            this.applyFadeEffect();
        }

        if (this.options.auto) {
            this.startAutoplay();
        }

        this.addEventListeners();
    }

    createPagination() {
        let paginationContainer = this.element.querySelector('.farlo-ui-revolver-carousel__pagination');

        // If paginationContainer doesn't exist, create it
        if (!paginationContainer) {
            paginationContainer = document.createElement('div');
            paginationContainer.className = 'farlo-ui-revolver-carousel__pagination';
            this.element.appendChild(paginationContainer);
        }

        // Clear existing dots
        paginationContainer.innerHTML = '';

        for (let i = 0; i < this.totalVisibleSets; i += 1) {
            const dot = document.createElement('button');
            dot.className = 'farlo-ui-revolver-carousel__pagination-sightdot';
            dot.setAttribute('aria-label', `Group ${i + 1}`);

            // Create a thumbnail for the dot
            const thumbnail = document.createElement('img');
            const slideIndex = i * this.visibleSlides;
            if (this.slides[slideIndex]) {
                const img = this.slides[slideIndex].querySelector('img');
                if (img) {
                    thumbnail.src = img.src;
                }
            }
            thumbnail.className = 'farlo-ui-revolver-carousel__pagination-thumbnail';
            dot.appendChild(thumbnail);

            dot.addEventListener('click', () => this.goToSlide(i));
            paginationContainer.appendChild(dot);
        }

        // Set the first dot as active if needed
        this.updatePaginationDots();

        // Scroll to active thumbnail after creating pagination
        this.scrollToActiveThumbnail();
    }

    createArrows() {
        const prevArrow = document.createElement('button');
        prevArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--prev';
        prevArrow.setAttribute('aria-label', 'Previous Slide');
        prevArrow.innerText = '<';
        prevArrow.addEventListener('click', () => this.prevSlide());

        const nextArrow = document.createElement('button');
        nextArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--next';
        nextArrow.setAttribute('aria-label', 'Next Slide');
        nextArrow.innerText = '>';
        nextArrow.addEventListener('click', () => this.nextSlide());

        // Remove any existing arrows to prevent duplication
        const existingPrevArrow = this.element.querySelector('.farlo-ui-revolver-carousel__trigger--prev');
        const existingNextArrow = this.element.querySelector('.farlo-ui-revolver-carousel__trigger--next');
        if (existingPrevArrow) existingPrevArrow.remove();
        if (existingNextArrow) existingNextArrow.remove();

        this.element.appendChild(prevArrow);
        this.element.appendChild(nextArrow);
    }

    calculateVisibleSlides() {
        if (this.slides.length > 0) {
            const carouselWidth = this.element.offsetWidth;
            const slideStyle = window.getComputedStyle(this.slides[0]);
            const slideWidth = this.slides[0].offsetWidth + parseInt(slideStyle.marginLeft, 10) + parseInt(slideStyle.marginRight, 10);
            this.visibleSlides = Math.floor(carouselWidth / slideWidth);
        }
    }

    updateTotalVisibleSets() {
        this.totalVisibleSets = Math.ceil(this.slides.length / this.visibleSlides);
    }

    applyFadeEffect() {
        this.track.classList.add('farlo-ui-revolver-carousel__cylinder--smoke');
    }

    addEventListeners() {
        if (!this.listenersAdded) {
            // Touch events
            this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), false);
            this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), false);

            // Add keyboard event listener
            this.element.setAttribute('tabindex', '0'); // Make the element focusable
            this.element.addEventListener('focus', () => {
                this.element.addEventListener('keydown', this.handleKeyboardInput);
            });
            this.element.addEventListener('blur', () => {
                this.element.removeEventListener('keydown', this.handleKeyboardInput);
            });

            if (this.options.swipe) {
                this.element.addEventListener('mousedown', this.startDrag.bind(this));
                this.element.addEventListener('mousemove', this.dragging.bind(this));
                this.element.addEventListener('mouseup', this.endDrag.bind(this));
                this.element.addEventListener('mouseleave', this.endDrag.bind(this));
            }

            // Resize event for responsive adjustments
            window.addEventListener('resize', () => {
                clearTimeout(this.resizeTimeout);
                this.resizeTimeout = setTimeout(() => {
                    this.onResize();
                }, 250); // Adjust debounce time as needed
            });

            this.listenersAdded = true;
        }
    }

    handleKeyboardInput(event) {
        switch (event.key) {
            case 'ArrowLeft':
                this.prevSlide();
                break;
            case 'ArrowRight':
                this.nextSlide();
                break;
        }
    }

    // Touch Handlers
    handleTouchStart(event) {
        this.startX = event.touches[0].clientX;
    }

    handleTouchEnd(event) {
        const endX = event.changedTouches[0].clientX;
        const deltaX = endX - this.startX;

        if (Math.abs(deltaX) > 50) { // Minimum swipe distance
            if (deltaX > 0) {
                this.prevSlide();
            } else {
                this.nextSlide();
            }
        }
    }

    // Drag Handlers
    startDrag(event) {
        event.preventDefault(); // Prevent default to enable drag
        this.isDragging = true;
        this.dragStartX = event.type.includes('mouse') ? event.pageX : event.touches[0].pageX;
    }

    dragging(event) {
        if (!this.isDragging) {
            return;
        }
        this.dragEndX = event.type.includes('mouse') ? event.pageX : event.touches[0].pageX;
    }

    endDrag(event) {
        if (!this.isDragging) {
            return;
        }

        this.isDragging = false;

        const delta = this.dragStartX - this.dragEndX;

        if (Math.abs(delta) > 30) {
            delta > 0 ? this.nextSlide() : this.prevSlide();
        }
    }

    onResize() {
        this.calculateVisibleSlides();
        this.updateTotalVisibleSets();

        // Reset currentIndex if it exceeds the new total number of sets
        if (this.currentIndex >= this.totalVisibleSets) {
            this.currentIndex = this.totalVisibleSets - 1;
        }

        // Update the carousel and pagination
        this.updateCarousel();

        if (this.options.pagination) {
            this.createPagination();
        }
    }

    goToSlide(index) {
        this.currentIndex = index;
        this.updateCarousel();
    }

    nextSlide() {
        // Move to the next set, or to the last set if we reach the end
        this.currentIndex = this.currentIndex < this.totalVisibleSets - 1 ? this.currentIndex + 1 : (this.options.loop ? 0 : this.currentIndex);
        this.updateCarousel();
        console.log(this.currentIndex);
    }

    prevSlide() {
        this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : (this.options.loop ? this.totalVisibleSets - 1 : this.currentIndex);
        this.updateCarousel();
        console.log(this.currentIndex);
    }

    startAutoplay() {
        this.autoplayInterval = setInterval(() => {
            this.nextSlide();
        }, this.options.autoDuration);
    }

	restartAutoplay() {
		this.stopAutoplay();
		this.startAutoplay();
	}

    stopAutoplay() {
        clearInterval(this.autoplayInterval);
    }

    updatePaginationDots() {
        const dots = this.element.querySelectorAll('.farlo-ui-revolver-carousel__pagination-sightdot');

        dots.forEach((dot, index) => {
            dot.classList.toggle('farlo-ui-revolver-carousel__pagination-sightdot--active', index === this.currentIndex);
        });
    }

    updateCarousel() {
        if (this.options.auto) {
            this.restartAutoplay();
        }

        let transformIndex = this.currentIndex * this.visibleSlides;

        // For the last set, adjust the transform to show the last slides
        if (this.currentIndex === this.totalVisibleSets - 1 && this.slides.length % this.visibleSlides !== 0) {
            transformIndex = this.slides.length - this.visibleSlides;
        }

        // Calculate the transform value based on the slide width
        const slideStyle = window.getComputedStyle(this.slides[0]);
        const slideWidth = this.slides[0].offsetWidth + parseInt(slideStyle.marginLeft, 10) + parseInt(slideStyle.marginRight, 10);
        const transformValue = -(transformIndex * slideWidth);

        this.track.style.transform = `translateX(${transformValue}px)`;

        this.slides.forEach((slide, index) => {
            slide.classList.toggle('farlo-ui-revolver__chamber--active', index >= transformIndex && index < transformIndex + this.visibleSlides);
            slide.setAttribute('aria-hidden', !(index >= transformIndex && index < transformIndex + this.visibleSlides));
            if (this.options.fade) {
                slide.style.opacity = this.options.fade && index >= transformIndex && index < transformIndex + this.visibleSlides ? '1' : '0';
            }
        });

        this.updatePaginationDots();
        this.scrollToActiveThumbnail();
    }

    scrollToActiveThumbnail() {
        const paginationContainer = this.element.querySelector('.farlo-ui-revolver-carousel__pagination');

        if (!paginationContainer) {
            return;
        }

        const activeDot = paginationContainer.querySelector('.farlo-ui-revolver-carousel__pagination-sightdot--active');

        if (activeDot) {
            const containerWidth = paginationContainer.offsetWidth;
            const activeDotLeft = activeDot.offsetLeft;
            const activeDotWidth = activeDot.offsetWidth;

            paginationContainer.scrollTo({
                left: activeDotLeft - (containerWidth / 2) + (activeDotWidth / 2),
                behavior: 'smooth'
            });
        }
    }
}

export default FarloUiRevolverReviewCarousel;
